import { useMemo } from 'react';

import type { Fetcher } from 'api/fetcher';
import type { GenericNodeWithTechnicalName } from 'api/meta/templateModel';
import { useApi } from 'api/useApi';
import { ENV_VARS } from 'app/config/environment-vars';
import type { IChange } from 'state/selectedTemplate/changes/templateChanges';
import type { IHaveId } from 'state/selectedTemplate/selectedTemplateStore';
import type { Template } from 'state/selectedTemplate/selectedTemplateStore';
import type { TemplateSummary } from 'state/templates/templateState';
import { pathJoin } from 'utils/strings/pathJoin';

export function useTemplatesApi() {
  const fetchFunction = useApi(ENV_VARS.META_URL, 'MetaApiClient');

  return useMemo(() => templatesApi(fetchFunction), [fetchFunction]);
}

const templatesApi = (fetchFunction: Fetcher) => {
  const getAll = (getOnlyLatest: boolean) =>
    fetchFunction<TemplateSummary[]>(`api/template?onlyLatest=${getOnlyLatest}`);

  const getById = (templateId: string) => fetchFunction<Template>(pathJoin('api/template', templateId));

  const updateName = (templateId: string, newName: string) =>
    fetchFunction<GenericNodeWithTechnicalName>(pathJoin('api/template', templateId), {
      method: 'PATCH',
      body: JSON.stringify({ newName })
    });

  const setReleaseNotes = (templateId: string, releaseNotes: string) =>
    fetchFunction<IHaveId>(pathJoin('api/template', templateId, 'releasenotes'), {
      method: 'PATCH',
      body: JSON.stringify({ releaseNotes })
    });

  const setUnReleased = (templateId: string) =>
    fetchFunction<IHaveId>(pathJoin('api/template', templateId) + ':not-released', {
      method: 'POST'
    });

  const remove = (templateId: string, deletePreviousVersions = false) =>
    fetchFunction<GenericNodeWithTechnicalName>(
      pathJoin('api/template', templateId) + `?deletePreviousVersion=${deletePreviousVersions}`,
      { method: 'DELETE' }
    );

  const getChanges = (templateId: string) => fetchFunction<IChange[]>(pathJoin('api/template', templateId, 'changes'));

  return {
    /** Get all templates. */
    getAll,
    /** Get the template by Id. */
    getById,
    /** Update template name. */
    updateName,
    /** Set template release notes. */
    setReleaseNotes,
    /** Set template as unreleased. */
    setUnReleased,
    /** Remove the template by Id. */
    remove,
    /** Get template changes. */
    getChanges
  };
};
